import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Stethoscope, Loader2, Plus, ShieldAlert } from "lucide-react";

const TYPES = { embauche: "Embauche", periodique: "Périodique", reprise: "Reprise", occasionnelle: "Occasionnelle" };

export default function Medical() {
  const [visites, setVisites] = useState([]);
  const [docs, setDocs] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ salarie_id: "", type: "embauche", date_visite: new Date().toISOString().slice(0,10), medecin: "", apte: true, restrictions: "", prochaine_visite: "" });

  const load = async () => {
    setLoading(true);
    try {
      const [v, d, s] = await Promise.all([api.get("/medical/visites"), api.get("/medical/documents"), api.get("/salaries")]);
      setVisites(v.data); setDocs(d.data); setSalaries(s.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/medical/visites", form);
      toast.success("Visite enregistrée"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const salLabel = (id) => { const s = salaries.find(x => x.id === id); return s ? `${s.prenom} ${s.nom}` : "—"; };

  return (
    <div data-testid="medical-page">
      <PageHeader
        title="Espace médical restreint"
        subtitle="Accès réservé · Aucun diagnostic n'est stocké, uniquement aptitudes et restrictions."
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="med-add-btn" className="bg-[#C5A059] hover:bg-[#A88846] text-white">
                <Plus className="h-4 w-4 mr-2" /> Nouvelle visite
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Visite médicale (sans diagnostic)</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="grid grid-cols-2 gap-4">
                <div className="space-y-2 col-span-2">
                  <Label>Salarié *</Label>
                  <Select value={form.salarie_id} onValueChange={(v)=>setForm({...form, salarie_id: v})}>
                    <SelectTrigger data-testid="med-salarie"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                    <SelectContent>{salaries.map(s=><SelectItem key={s.id} value={s.id}>{s.prenom} {s.nom}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Type *</Label>
                  <Select value={form.type} onValueChange={(v)=>setForm({...form, type: v})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(TYPES).map(([k,v])=><SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Date visite *</Label><Input type="date" required value={form.date_visite} onChange={(e)=>setForm({...form, date_visite: e.target.value})} /></div>
                <div className="space-y-2 col-span-2"><Label>Médecin du travail</Label><Input value={form.medecin} onChange={(e)=>setForm({...form, medecin: e.target.value})} /></div>
                <div className="space-y-2">
                  <Label>Apte ?</Label>
                  <Select value={form.apte ? "1" : "0"} onValueChange={(v)=>setForm({...form, apte: v === "1"})}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Apte</SelectItem>
                      <SelectItem value="0">Inapte / restrictions</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Prochaine visite</Label><Input type="date" value={form.prochaine_visite} onChange={(e)=>setForm({...form, prochaine_visite: e.target.value})} /></div>
                <div className="space-y-2 col-span-2">
                  <Label>Restrictions / aptitudes <span className="text-red-500 text-xs">(jamais de diagnostic)</span></Label>
                  <Textarea rows={3} value={form.restrictions} onChange={(e)=>setForm({...form, restrictions: e.target.value})} placeholder="Ex. Port de charge limité à 10 kg, télétravail possible..." />
                </div>
                <DialogFooter className="col-span-2"><Button type="submit" data-testid="med-submit" className="bg-[#C5A059] hover:bg-[#A88846] text-white">Enregistrer</Button></DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4 flex gap-3 items-start" data-testid="med-rgpd-banner">
        <ShieldAlert className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
        <div className="text-sm text-red-800">
          <strong>Conformité RGPD & secret médical</strong> — Cet espace ne stocke <strong>aucun diagnostic</strong>. Seules les conclusions d&apos;aptitude au poste, restrictions et dates de visite sont conservées. Accès limité aux rôles <code className="bg-red-100 px-1 rounded">medical</code> et <code className="bg-red-100 px-1 rounded">admin</code>.
        </div>
      </div>

      {loading ? <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#C5A059]" /></div> : (
        <Tabs defaultValue="visites">
          <TabsList className="bg-white border border-slate-200">
            <TabsTrigger value="visites" data-testid="tab-med-visites">Visites médicales</TabsTrigger>
            <TabsTrigger value="docs" data-testid="tab-med-docs">Documents restreints</TabsTrigger>
          </TabsList>

          <TabsContent value="visites" className="mt-5">
            {visites.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
                <Stethoscope className="h-10 w-10 mx-auto text-slate-300" />
                <p className="mt-3 text-slate-500">Aucune visite enregistrée.</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200"><tr className="text-left text-xs uppercase tracking-wider text-slate-500"><th className="px-4 py-3">Date</th><th className="px-4 py-3">Salarié</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Médecin</th><th className="px-4 py-3">Aptitude</th><th className="px-4 py-3">Restrictions</th><th className="px-4 py-3">Prochaine</th></tr></thead>
                  <tbody>{visites.map(v => (
                    <tr key={v.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="px-4 py-3 text-slate-700">{v.date_visite}</td>
                      <td className="px-4 py-3 font-medium">{salLabel(v.salarie_id)}</td>
                      <td className="px-4 py-3 text-slate-600">{TYPES[v.type] || v.type}</td>
                      <td className="px-4 py-3 text-slate-600">{v.medecin || "—"}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${v.apte ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{v.apte ? "Apte" : "Restrictions"}</span></td>
                      <td className="px-4 py-3 text-slate-600 text-xs truncate max-w-xs">{v.restrictions || "—"}</td>
                      <td className="px-4 py-3 text-slate-600 text-xs">{v.prochaine_visite || "—"}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="docs" className="mt-5">
            {docs.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 p-12 text-center">
                <Stethoscope className="h-10 w-10 mx-auto text-slate-300" />
                <p className="mt-3 text-slate-500">Aucun document médical restreint.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {docs.map(d => (
                  <div key={d.id} className="kpi-card bg-white border border-red-200 rounded-xl p-5">
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-red-600">Médical restreint · v{d.version}</div>
                    <h3 className="mt-2 font-semibold text-slate-900">{d.titre}</h3>
                    <div className="mt-2 text-xs text-slate-500 truncate">{d.original_filename}</div>
                    <div className="text-[11px] text-slate-400 mt-1">{Math.round((d.size||0)/1024)} Ko · {d.created_at?.slice(0,10)}</div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
